import { get, includes } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import TagManager from 'react-gtm-module';
import actions from './actions';
import vendorActions from '../VendorApplication/actions';
import { actions as userActions } from '../User/slice';
import BasicVendorRegistrationForm from '../BasicRegistration/BasicRegistration';
import Role, { getAllowedRoleNames } from '../../Lib/Constants/roles.ts';
import Login from '../Login/Login';
import Admin from '../Admin/Admin';
import Deliveries from '../Deliveries/Deliveries';
import DeliveryManagement from '../Deliveries/DeliveryManagement';
import PublicFiles from '../PublicFiles/PublicFiles';
import PurchaseOrderList from '../../Components/PurchaseOrders/PurchaseOrderList.tsx';
import PrivateRoute from '../../Components/PrivateRoute/PrivateRoute';
import SideBar from '../../Components/SideBar/SideBar';
import locationProps from '../../Lib/Utils/PropTypes/location-props';
import Dashboard from '../Dashboard/Dashboard';
import VendorApplication from '../VendorApplication/VendorApplication';
import VendorApplications from '../VendorApplications/VendorApplications';
import PurchaseOrderDetail from '../../Components/PurchaseOrderDetail/PurchaseOrderDetail.tsx';
import RequestedItems from '../RequestedItems/RequestedItems';
import RequestedItemsV2 from '../RequestedItemsV2/RequestedItemsV2';
import BillPayments from '../BillPayments/BillPayments';
import BillPaymentDetail from '../BillPayments/BillPaymentDetail';
import BillCredits from '../BillCredits/BillCredits';
import BillCreditDetail from '../BillCredits/BillCreditDetail';
import VendorBills from '../VendorBills/VendorBills';
import VendorBillDetail from '../VendorBills/VendorBillDetail';
import VendorApplicationInit from '../VendorApplicationInit/VendorApplicationInit';
import FnCatalog from '../FnCatalog/FnCatalog';
import Maintenance from '../../Components/Maintenance/Maintenance';
import { Navigation } from '../../Components/Navigation/Navigation.tsx';
import FeatureFlags from '../../Components/Admin/FeatureFlags.tsx';
import {
  currentUserSelector,
  loggedInSelector,
  ssoLoggedInSelector,
} from './selectors';
import {
  publicFilesUrlsSelector,
  vendorLoadingSelector,
  vendorSelector,
} from '../VendorApplication/selectors';
import VendorDeliveries from '../Deliveries/VendorDeliveries';
import PublicVendorAttribution from '../VendorAttribution/PublicVendorAttribution';
import ApplicationStatus from '../../Lib/Constants/vendor-application-status';
import { getApplicationStatus } from '../../Lib/Utils/FormUtils/vendor-application';
import InfluencerProfile from '../VendorApplication/InfluencerProfile';
import InfluencerUserInfo from '../InfluencerUserInfo/InfluencerUserInfo';
import { userSelector } from '../User/selectors';
import InfluencerInitializer from '../VendorInitializer/InfluencerInitializer';
import InfluencerBasicRegistration from '../BasicRegistration/InfluencerBasicRegistration';
import SocialMedia from '../SocialMedia/SocialMedia';
import Compliance from '../Compliance/Compliance';
import VendorFactories from '../Factory/VendorFactories';
import VendorFactory from '../Factory/VendorFactory';
import ComplianceFactories from '../Factory/ComplianceFactories';
import PasswordReset from '../PasswordReset/PasswordReset';
import InviteTokens from '../InviteToken/InviteTokens';
import urlToTitle from './urlToTitle';
import VendorApplicationOnboarding from '../VendorApplicationOnboarding/VendorApplicationOnboarding';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext.tsx';
import { withCurrentUser } from '../../Lib/Contexts/CurrentUserContext.tsx';
import { PageNotFound } from '../../Components/PageNotFound/PageNotFound.tsx';
import { PageLoading } from '../../Components/PageLoading/PageLoading.tsx';
import VendorTeamMembers from '../TeamMembers/VendorTeamMembers';
import HiddenChatAdmin from '../HiddenChatAdmin/HiddenChatAdmin';
import HiddenChatUser from '../HiddenChatUser/HiddenChatUser';
import { PrintingSetupPage } from '../../Components/PrintingSetup/PrintingSetup.tsx';
import { PrintingSetupPageV2 } from '../../Components/PrintingSetup/PrintingSetupV2.tsx';
import DevTools from '../../Components/DevTool/DevTool.tsx';
import ASN from '../../Components/ASN/ASN.tsx';
import { HelpCenterPage } from '../../Components/HelpCenter/HelpCenter.tsx';
import PermissionsOverview from '../../Components/Admin/PermissionsOverview.tsx';
import SellToUs from '../../Components/SellToUs/index.tsx';
import SampleTags from '../../Components/SampleTags/index.tsx';
import { AnnouncementLog } from '../../Components/HelpCenter/AnnouncementLog.tsx';
import NewItemRequests from '../../Components/NewItemRequests/NewItemRequests.tsx';
import ItemDetailsPage from '../../Components/SellToUs/ItemDetails/ItemDetailsPage.tsx';
import ItemDetailsLayout from '../../Components/SellToUs/ItemDetails/ItemDetailsLayout.tsx';
import { hasLegacyPrintingApproval } from '../../Lib/Utils/Asn/printingApproved.ts';
/**
 * Renders app component
 */
class App extends Component {
  constructor(props) {
    super(props);

    if (props.isLoggedIn) {
      props.getCurrentUser();
    } else if (!props.isSSOLoggedIn) {
      // Check if user has logged in through OneLogin
      props.getCurrentOneLoginUser();
    }
    props.history.listen((location) => {
      document.title = urlToTitle(location.pathname);
    });
  }

  componentDidMount() {
    TagManager.initialize({ gtmId: 'GTM-MMZF69K' });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.currentUser !== this.props.currentUser) {
      this.props.setCurrentUser(this.props.currentUser);
      const sentryUser =
        this.props.currentUser && Object.keys(this.props.currentUser).length > 0
          ? {
              id: this.props.currentUser.id,
              role: this.props.currentUser.role,
              vendor_id: this.props.currentUser.vendor?.id,
              feature_flag_access: this.props.currentUser.feature_flag_access,
              feature_permissions: this.props.currentUser.feature_permissions,
            }
          : null;
      Sentry.setUser(sentryUser);
    }
  }

  render() {
    const {
      isLoggedIn,
      isSSOLoggedIn,
      logout,
      currentUser,
      location,
      getVendor,
      vendor,
      vendorLoading,
      getUser,
      user,
      publicFilesUrls,
      getPublicFiles,
    } = this.props;
    const applicationStatus = getApplicationStatus(currentUser.vendor || {});
    const approved = ApplicationStatus.APPROVED === applicationStatus;
    const isInfluencerAdmin = includes(
      [Role.SOCIAL_MEDIA_OPS, Role.SOCIAL_MEDIA_MANAGER],
      get(currentUser, 'role'),
    );
    const isSchedulerRole = includes(
      [Role.SCHEDULER, Role.PRODUCT_DEVELOPMENT],
      get(currentUser, 'role'),
    );
    const isInternalVendorOnboardingRole = includes(
      [Role.PRODUCT_DEVELOPMENT, Role.BUYER, Role.SPONSOR, Role.EXECUTIVE],
      get(currentUser, 'role'),
    );
    const isCreatorsDomain = window.location.hostname.includes('creators');
    const { flags } = this.context;

    return (
      <div className="h-full w-full overflow-auto">
        <Switch>
          {flags.maintenance && flags.maintenance.enabled && (
            <>
              <Route path="/maintenance" exact component={Maintenance} />
              <Redirect to="/maintenance" />
            </>
          )}
          <Route
            path="/influencer-register"
            exact
            isLoggedIn={isLoggedIn}
            component={InfluencerBasicRegistration}
          />
          <Route
            path="/influencer-password-reset"
            exact
            isLoggedIn={isLoggedIn}
            render={(props) => <PasswordReset {...props} influencer />}
          />
          <Route
            path="/register-basic"
            exact
            isLoggedIn={isLoggedIn}
            component={BasicVendorRegistrationForm}
          />
          <Route
            path="/influencer-login"
            exact
            isLoggedIn={isLoggedIn}
            render={(props) => <Login {...props} influencer />}
          />
          <Route
            path="/login"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            render={(props) => <Login {...props} />}
          />
          <Route
            path="/password-reset"
            exact
            isLoggedIn={isLoggedIn}
            render={(props) => <PasswordReset {...props} />}
          />
          <Route
            path="/vendor-form"
            exact
            isLoggedIn={isLoggedIn}
            component={PublicVendorAttribution}
          />
          {!isLoggedIn && isCreatorsDomain && (
            <Redirect to="/influencer-login" />
          )}
          {!isLoggedIn && !isSSOLoggedIn && <Redirect to="/login" />}
          {currentUser?.feature_permissions?.dev_tools ? (
            <PrivateRoute
              path="/dev-tools"
              exact
              isLoggedIn={isLoggedIn}
              isSSOLoggedIn={isSSOLoggedIn}
              currentUser={currentUser}
              render={(props) => (
                <Navigation currentUser={currentUser} logout={logout}>
                  <DevTools {...props} />
                </Navigation>
              )}
              allowedRoles={getAllowedRoleNames(['dev-tools'])}
            />
          ) : null}
          <PrivateRoute
            path="/admin-console"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            allowedRoles={getAllowedRoleNames('admin-console')}
            render={(props) => (
              <Navigation currentUser={currentUser} logout={logout}>
                <Admin {...props} />
              </Navigation>
            )}
          />
          <PrivateRoute
            path="/admin-console/feature-flags"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            allowedRoles={getAllowedRoleNames('admin-console')}
            render={(props) => (
              <Navigation currentUser={currentUser} logout={logout}>
                <FeatureFlags {...props} />
              </Navigation>
            )}
          />
          <PrivateRoute
            path="/admin-console/permissions"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            allowedRoles={getAllowedRoleNames('admin-console/permissions')}
            render={(props) => (
              <Navigation currentUser={currentUser} logout={logout}>
                <PermissionsOverview {...props} />
              </Navigation>
            )}
          />
          <PrivateRoute
            path="/chat-hidden"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            allowedRoles={getAllowedRoleNames('chat-hidden')}
            render={(props) => (
              <Navigation currentUser={currentUser} logout={logout}>
                <HiddenChatAdmin {...props} />
              </Navigation>
            )}
          />
          <PrivateRoute
            path="/vendor-applications"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            render={(props) => (
              <Navigation currentUser={currentUser} logout={logout}>
                <VendorApplications {...props} />
              </Navigation>
            )}
            allowedRoles={getAllowedRoleNames('vendor-applications')}
          />
          <PrivateRoute
            path="/vendor-applications/initiate-vendor-application"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            component={VendorApplicationInit}
            allowedRoles={getAllowedRoleNames(
              'vendor-applications/initiate-vendor-application',
            )}
          />
          <PrivateRoute
            path="/vendor-applications/:vendorId/vendor-application-onboarding"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            component={VendorApplicationOnboarding}
            allowedRoles={getAllowedRoleNames(
              'vendor-applications/:vendorId/vendor-application-onboarding',
            )}
          />
          <PrivateRoute
            path="/compliance-console"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            render={(props) => (
              <Navigation currentUser={currentUser} logout={logout}>
                <Compliance {...props} />
              </Navigation>
            )}
            allowedRoles={getAllowedRoleNames('compliance-console')}
          />
          <PrivateRoute
            path="/compliance-factories"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            render={(props) => (
              <Navigation currentUser={currentUser} logout={logout}>
                <ComplianceFactories {...props} />
              </Navigation>
            )}
            allowedRoles={getAllowedRoleNames('compliance-factories')}
          />
          <PrivateRoute
            path="/admin-console/public-files"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            allowedRoles={getAllowedRoleNames('admin-console/public-files')}
            render={(props) => (
              <Navigation currentUser={currentUser} logout={logout}>
                <PublicFiles {...props} />
              </Navigation>
            )}
          />
          <Route
            path="/social-media-console"
            render={() => (
              <Navigation currentUser={currentUser} logout={logout}>
                <Switch>
                  <PrivateRoute
                    path="/social-media-console"
                    exact
                    isLoggedIn={isLoggedIn}
                    isSSOLoggedIn={isSSOLoggedIn}
                    currentUser={currentUser}
                    component={SocialMedia}
                    allowedRoles={getAllowedRoleNames('social-media-console')}
                  />

                  <PrivateRoute
                    path="/social-media-console/influencer-init"
                    exact
                    isLoggedIn={isLoggedIn}
                    isSSOLoggedIn={isSSOLoggedIn}
                    currentUser={currentUser}
                    component={InfluencerInitializer}
                    allowedRoles={getAllowedRoleNames(
                      'social-media-console/influencer-init',
                    )}
                  />

                  <PrivateRoute
                    path="/social-media-console/invite-tokens"
                    exact
                    isLoggedIn={isLoggedIn}
                    isSSOLoggedIn={isSSOLoggedIn}
                    currentUser={currentUser}
                    component={InviteTokens}
                    allowedRoles={getAllowedRoleNames(
                      'social-media-console/invite-tokens',
                    )}
                  />

                  <PrivateRoute
                    location={location}
                    isLoggedIn={isLoggedIn}
                    isSSOLoggedIn={isSSOLoggedIn}
                    currentUser={currentUser}
                    component={PageNotFound}
                  />
                </Switch>
              </Navigation>
            )}
          />
          <PrivateRoute
            path="/deliveries"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            allowedRoles={getAllowedRoleNames('deliveries')}
            render={(props) => (
              <Navigation currentUser={currentUser} logout={logout}>
                <Deliveries {...props} />
              </Navigation>
            )}
          />
          <PrivateRoute
            path="/delivery-management"
            exact
            isLoggedIn={isLoggedIn}
            isSSOLoggedIn={isSSOLoggedIn}
            currentUser={currentUser}
            allowedRoles={getAllowedRoleNames('delivery-management')}
            render={(props) => (
              <Navigation currentUser={currentUser} logout={logout}>
                <DeliveryManagement {...props} />
              </Navigation>
            )}
          />
          {flags?.generate_asn?.enabled && (
            <PrivateRoute
              path="/users/:id/purchase-orders/:purchaseOrderNumber/asn"
              exact
              location={location}
              isLoggedIn={isLoggedIn}
              isSSOLoggedIn={isSSOLoggedIn}
              currentUser={currentUser}
              render={(props) => <ASN {...props} user={user} />}
              allowedRoles={getAllowedRoleNames(
                'users/:id/purchase-orders/:purchaseOrderNumber/asn',
              )}
            />
          )}
          <Route
            path="/users"
            render={() => (
              <Switch>
                {flags?.vendor_catalog?.enabled && (
                  <Route
                    path="/users/:id/sell-to-us/:itemId"
                    exact
                    render={() => (
                      <ItemDetailsLayout>
                        <PrivateRoute
                          path="/users/:id/sell-to-us/:itemId"
                          exact
                          location={location}
                          isLoggedIn={isLoggedIn}
                          isSSOLoggedIn={isSSOLoggedIn}
                          currentUser={currentUser}
                          render={(props) => (
                            <ItemDetailsPage {...props} user={user} />
                          )}
                          allowedRoles={getAllowedRoleNames(
                            'users/:id/sell-to-us/:itemId',
                          )}
                        />
                      </ItemDetailsLayout>
                    )}
                  />
                )}
                <SideBar
                  loading={vendorLoading}
                  id={currentUser.id}
                  currentUser={currentUser}
                  getVendor={getVendor}
                  getUser={getUser}
                  getPublicFiles={getPublicFiles}
                  location={location}
                  vendor={vendor}
                  user={user}
                  logout={logout}
                  publicFilesUrls={publicFilesUrls}
                >
                  <Switch>
                    <PrivateRoute
                      path="/users/:id/dashboard"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={Dashboard}
                      allowedRoles={getAllowedRoleNames('users/:id/dashboard')}
                    />

                    <PrivateRoute
                      path="/users/:id/chat-hidden"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={HiddenChatUser}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/chat-hidden',
                      )}
                    />

                    <PrivateRoute
                      path="/users/:id/profile"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={VendorApplication}
                      allowedRoles={getAllowedRoleNames('users/:id/profile')}
                    />

                    <PrivateRoute
                      path="/users/:id/influencer-payee-information"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={InfluencerProfile}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/influencer-payee-information',
                      )}
                    />

                    <PrivateRoute
                      path="/users/:id/influencer-user-info"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={InfluencerUserInfo}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/influencer-user-info',
                      )}
                    />

                    <PrivateRoute
                      path="/users/:id/purchase-orders"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      render={(props) => (
                        <PurchaseOrderList {...props} user={user} />
                      )}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/purchase-orders',
                      )}
                    />

                    <PrivateRoute
                      path="/users/:id/purchase-orders/:purchaseOrderId"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      render={(props) => (
                        <PurchaseOrderDetail {...props} user={user} />
                      )}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/purchase-orders',
                      )}
                    />

                    {flags?.vendor_catalog?.enabled && (
                      <PrivateRoute
                        path="/users/:id/sell-to-us"
                        exact
                        location={location}
                        isLoggedIn={isLoggedIn}
                        isSSOLoggedIn={isSSOLoggedIn}
                        currentUser={currentUser}
                        render={(props) => <SellToUs {...props} user={user} />}
                        allowedRoles={getAllowedRoleNames(
                          'users/:id/sell-to-us',
                        )}
                      />
                    )}
                    {flags?.sample_tags?.enabled && (
                      <PrivateRoute
                        path="/users/:id/sample-tags"
                        exact
                        location={location}
                        isLoggedIn={isLoggedIn}
                        isSSOLoggedIn={isSSOLoggedIn}
                        currentUser={currentUser}
                        render={(props) => (
                          <SampleTags {...props} user={user} />
                        )}
                        allowedRoles={getAllowedRoleNames(
                          'users/:id/sample-tags',
                        )}
                      />
                    )}

                    <PrivateRoute
                      path="/users/:id/sample-requests"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={RequestedItemsV2}
                      render={(props) => (
                        <RequestedItemsV2
                          {...props}
                          currentUser={currentUser}
                        />
                      )}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/requested-items',
                      )}
                    />

                    <PrivateRoute
                      path="/users/:id/requested-items"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={RequestedItems}
                      render={(props) => (
                        <RequestedItems {...props} currentUser={currentUser} />
                      )}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/requested-items',
                      )}
                    />

                    <PrivateRoute
                      path="/users/:id/payments"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={BillPayments}
                      allowedRoles={getAllowedRoleNames('users/:id/payments')}
                    />

                    <PrivateRoute
                      path="/users/:id/payments/:billPaymentId"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={BillPaymentDetail}
                      allowedRoles={getAllowedRoleNames('users/:id/payments')}
                    />

                    <PrivateRoute
                      path="/users/:id/invoices"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={VendorBills}
                      allowedRoles={getAllowedRoleNames('users/:id/invoices')}
                    />

                    <PrivateRoute
                      path="/users/:id/invoices/:vendorBillId"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={VendorBillDetail}
                      allowedRoles={getAllowedRoleNames('users/:id/invoices')}
                    />

                    <PrivateRoute
                      path="/users/:id/deliveries"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={VendorDeliveries}
                      allowedRoles={getAllowedRoleNames('users/:id/deliveries')}
                    />

                    <PrivateRoute
                      path="/users/:id/credits"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={BillCredits}
                      allowedRoles={getAllowedRoleNames('users/:id/credits')}
                    />

                    <PrivateRoute
                      path="/users/:id/credits/:billCreditId"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={BillCreditDetail}
                      allowedRoles={getAllowedRoleNames('users/:id/credits')}
                    />

                    <PrivateRoute
                      path="/users/:id/factories"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={VendorFactories}
                      allowedRoles={getAllowedRoleNames('users/:id/factories')}
                    />

                    <PrivateRoute
                      path="/users/:id/team-members"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={VendorTeamMembers}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/team-members',
                      )}
                    />

                    <PrivateRoute
                      path="/users/:id/factories/:factoryId"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={VendorFactory}
                      allowedRoles={getAllowedRoleNames('users/:id/factories')}
                    />

                    {!(flags.prop65 && flags.prop65?.enabled) && (
                      <PrivateRoute
                        path="/users/:id/fn-catalog"
                        exact
                        location={location}
                        isLoggedIn={isLoggedIn}
                        isSSOLoggedIn={isSSOLoggedIn}
                        currentUser={currentUser}
                        render={(props) => (
                          <FnCatalog {...props} user={currentUser} />
                        )}
                        allowedRoles={getAllowedRoleNames(
                          'users/:id/fn-catalog',
                        )}
                      />
                    )}

                    <PrivateRoute
                      path="/users/:id/printing-setup"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      render={(props) => {
                        if (hasLegacyPrintingApproval(user)) {
                          return (
                            <PrintingSetupPage
                              {...props}
                              publicFilesUrls={publicFilesUrls}
                              user={user}
                            />
                          );
                        }
                        return (
                          <PrintingSetupPageV2
                            {...props}
                            publicFilesUrls={publicFilesUrls}
                            user={user}
                          />
                        );
                      }}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/printing-setup',
                      )}
                    />

                    <PrivateRoute
                      path="/users/:id/help-center"
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      render={(props) => (
                        <HelpCenterPage {...props} user={user} />
                      )}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/help-center',
                      )}
                    />

                    <PrivateRoute
                      path="/users/:id/announcement-logs"
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      render={(props) => (
                        <AnnouncementLog {...props} user={user} />
                      )}
                      allowedRoles={getAllowedRoleNames(
                        'users/:id/announcement-logs',
                      )}
                    />

                    <PrivateRoute
                      path="/users/:id/new-item-requests"
                      exact
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      render={() => <NewItemRequests />}
                    />

                    <PrivateRoute
                      path="*"
                      location={location}
                      isLoggedIn={isLoggedIn}
                      isSSOLoggedIn={isSSOLoggedIn}
                      currentUser={currentUser}
                      component={PageNotFound}
                    />
                  </Switch>
                </SideBar>
              </Switch>
            )}
          />
          {/* TODO: Once SSO is fully implemented for all internal users,
          isLoggedIn can be removed for the next 5 routes */}
          {(isLoggedIn || isSSOLoggedIn) && currentUser.role === Role.ADMIN && (
            <Redirect to="/admin-console" />
          )}
          {(isLoggedIn || isSSOLoggedIn) &&
            currentUser.role === Role.AP_ADMIN && (
              <Redirect to="/admin-console" />
            )}
          {(isLoggedIn || isSSOLoggedIn) &&
            currentUser.role === Role.RECEIVER && <Redirect to="/deliveries" />}
          {(isLoggedIn || isSSOLoggedIn) &&
            currentUser.role === Role.COMPLIANCE && (
              <Redirect to="/compliance-console" />
            )}
          {(isLoggedIn || isSSOLoggedIn) && isInfluencerAdmin && (
            <Redirect to="/social-media-console" />
          )}
          {(isLoggedIn || isSSOLoggedIn) && isSchedulerRole && (
            <Redirect to="/deliveries" />
          )}
          {(isLoggedIn || isSSOLoggedIn) && isInternalVendorOnboardingRole && (
            <Redirect to="/vendor-applications" />
          )}
          {isLoggedIn &&
            currentUser.role === Role.VENDOR &&
            currentUser.vendor.application_status === 'Initiated' && (
              <Redirect
                to={`/vendor-applications/${currentUser.vendor.id}/vendor-application-onboarding`}
              />
            )}
          {isLoggedIn && currentUser.role === Role.VENDOR && approved && (
            <Redirect to={`/users/${currentUser.id}/dashboard`} />
          )}
          {isLoggedIn && currentUser.role === Role.VENDOR && (
            <Redirect to={`/users/${currentUser.id}/profile`} />
          )}
          {isLoggedIn && currentUser.role === Role.VENDOR_STAFF && approved && (
            <Redirect to={`/users/${currentUser.id}/requested-items`} />
          )}
          {isLoggedIn &&
            currentUser.role === Role.VENDOR_OPERATIONS &&
            approved && (
              <Redirect to={`/users/${currentUser.id}/purchase-orders`} />
            )}
          {isLoggedIn && currentUser.role === Role.INFLUENCER && (
            <Redirect
              to={`/users/${currentUser.id}/influencer-payee-information`}
            />
          )}
          <Route path="*" render={() => <PageLoading />} />
        </Switch>
      </div>
    );
  }
}

App.contextType = FeatureFlagsContext;

App.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  isSSOLoggedIn: PropTypes.bool.isRequired,
  logout: PropTypes.func.isRequired,
  location: locationProps.isRequired,
  getCurrentUser: PropTypes.func.isRequired,
  getCurrentOneLoginUser: PropTypes.func.isRequired,
  getVendor: PropTypes.func.isRequired,
  vendor: PropTypes.shape({}).isRequired,
  vendorLoading: PropTypes.bool.isRequired,
  currentUser: PropTypes.shape({
    role: PropTypes.string,
  }),
};

App.defaultProps = {
  currentUser: null,
};

/**
 * The app state to be provided to the app component
 *
 * @param {object} state global state
 */
const mapStateToProps = (state) => ({
  isLoggedIn: loggedInSelector(state),
  isSSOLoggedIn: ssoLoggedInSelector(state),
  currentUser: currentUserSelector(state),
  vendor: vendorSelector(state),
  vendorLoading: vendorLoadingSelector(state),
  user: userSelector(state),
  publicFilesUrls: publicFilesUrlsSelector(state),
});

/**
 * Actions to be dispatched
 *
 * @param {function} dispatch function to dispatch app actions
 */
const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
  getCurrentUser: () => dispatch(actions.getCurrentUser()),
  getCurrentOneLoginUser: () => dispatch(actions.getCurrentOneLoginUser()),
  getVendor: (id) => dispatch(vendorActions.getVendor(id)),
  getUser: (userId) => dispatch(userActions.getUser({ userId })),
  getPublicFiles: (getType) => dispatch(vendorActions.getPublicFiles(getType)),
});

/**
 * Connects app state to the App component
 *
 * @param {object} mapStateToProps app data object to become props for the App component
 * @param {object} mapDispatchToProps app actions to be dispatched to reducer
 */
export default withCurrentUser(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(App)),
);
